import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, Briefcase, Building2 } from "lucide-react";
import {
  getProjetos, getRoleFromToken,
  getTicketsViagemProjeto, putTicketViagemProjeto,
  getTicketsViagemDespesasGerais, putTicketViagemDespesasGerais,
} from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";

const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  success: "#10B981",
  danger: "#EF4444",
  subcategoriaBg: isLight ? "#F5F0FC" : "#1E1435",
});

const headerBtnStyle = {
  display: "flex", alignItems: "center", gap: 6,
  background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
  borderRadius: 8, padding: "7px 12px", cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
};

function inputStyle(C) {
  return {
    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: "9px 12px", fontSize: 13, color: C.text, fontFamily: "inherit", outline: "none", width: "100%",
  };
}

function btnStyle(color, secondary = false) {
  return {
    display: "flex", alignItems: "center", gap: 6,
    background: secondary ? "transparent" : color,
    border: secondary ? `1px solid ${color}` : "none",
    color: secondary ? color : "#fff",
    borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700,
    fontFamily: "inherit", cursor: "pointer",
  };
}

// Premissas · Viagens: consolida os dois tickets médios de viagem que hoje só existiam
// embutidos no modal "Lançar Viagem" de cada tela — ProjetoTicketViagem (Custos Gerais por
// Projeto, um valor por projeto) e DespesaTicketViagem (Despesas Gerais, um único valor
// "Backoffice" compartilhado por todos os outros departamentos). Os modais continuam
// mostrando os tickets (para a estimativa ao vivo), mas somente leitura — a edição agora só
// existe aqui.
export default function PremissasViagemView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();
  const isAdmin = getRoleFromToken() === "admin";

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)", color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· Premissas · Viagens</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={() => setIsLight((l) => !l)} style={headerBtnStyle}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />} {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button onClick={() => navigate("/inicio")} style={headerBtnStyle}>Menu Principal</button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: C.text }}>Premissas · Viagens</h1>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: C.muted }}>
              Ticket médio travado usado em todo lançamento de viagem (Alimentação, Hospedagem, Passagem Aérea e Transporte).
              {!isAdmin && " Somente admin edita — os demais perfis visualizam."}
            </p>
          </div>

          <SecaoTicketPorProjeto C={C} isAdmin={isAdmin} />
          <SecaoTicketBackoffice C={C} isAdmin={isAdmin} />
        </main>
      </div>
    </div>
  );
}

function SecaoTicketPorProjeto({ C, isAdmin }) {
  const [projetos, setProjetos] = useState([]);
  const [projetoId, setProjetoId] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [salvandoId, setSalvandoId] = useState(null);

  useEffect(() => {
    getProjetos().then((lista) => {
      setProjetos(lista || []);
      if (lista?.length) setProjetoId(lista[0].id);
    }).catch((e) => setErro(e.message));
  }, []);

  useEffect(() => {
    if (!projetoId) { setCarregando(false); return; }
    setCarregando(true);
    setErro("");
    getTicketsViagemProjeto(projetoId)
      .then(setTickets)
      .catch((e) => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [projetoId]);

  async function salvarTicket(categoriaId, valor) {
    setSalvandoId(categoriaId);
    setErro("");
    try {
      const atualizado = await putTicketViagemProjeto(projetoId, categoriaId, Number(valor));
      setTickets((lista) => lista.map((t) => (t.categoria_id === categoriaId ? atualizado : t)));
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvandoId(null);
    }
  }

  return (
    <section style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Briefcase size={15} color={C.brand} />
        <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.text }}>Por Projeto (Custos Gerais por Projeto)</h2>
      </div>

      <div style={{ maxWidth: 320 }}>
        <select value={projetoId || ""} onChange={(e) => setProjetoId(Number(e.target.value))} style={inputStyle(C)}>
          {projetos.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
        </select>
      </div>

      {erro && <p style={{ margin: 0, fontSize: 12, color: C.danger }}>{erro}</p>}

      {carregando ? (
        <p style={{ fontSize: 12, color: C.muted }}>Carregando…</p>
      ) : !projetos.length ? (
        <p style={{ fontSize: 12, color: C.muted }}>Nenhum projeto cadastrado.</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
          {tickets.map((t) => (
            <CartaoTicketViagem
              key={t.categoria_id} C={C} ticket={t} isAdmin={isAdmin}
              salvando={salvandoId === t.categoria_id}
              onSalvar={(v) => salvarTicket(t.categoria_id, v)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function SecaoTicketBackoffice({ C, isAdmin }) {
  const [tickets, setTickets] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [salvandoCodigo, setSalvandoCodigo] = useState(null);

  useEffect(() => {
    getTicketsViagemDespesasGerais()
      .then(setTickets)
      .catch((e) => setErro(e.message))
      .finally(() => setCarregando(false));
  }, []);

  async function salvarTicket(categoriaCodigo, valor) {
    setSalvandoCodigo(categoriaCodigo);
    setErro("");
    try {
      const atualizado = await putTicketViagemDespesasGerais(categoriaCodigo, Number(valor));
      setTickets((lista) => lista.map((t) => (t.categoria_codigo === categoriaCodigo ? atualizado : t)));
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvandoCodigo(null);
    }
  }

  return (
    <section style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Building2 size={15} color={C.brand} />
        <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.text }}>Backoffice (Despesas Gerais)</h2>
      </div>
      <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
        Único valor, compartilhado por todos os departamentos de Despesas Gerais (não é por departamento).
      </p>

      {erro && <p style={{ margin: 0, fontSize: 12, color: C.danger }}>{erro}</p>}

      {carregando ? (
        <p style={{ fontSize: 12, color: C.muted }}>Carregando…</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
          {tickets.map((t) => (
            <CartaoTicketViagem
              key={t.categoria_codigo} C={C} ticket={t} isAdmin={isAdmin}
              salvando={salvandoCodigo === t.categoria_codigo}
              onSalvar={(v) => salvarTicket(t.categoria_codigo, v)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// Ticket médio travado — leitura livre, edição só para admin (backend valida de novo via
// only_admin, isto aqui é só UX). Compartilhado pelas duas seções acima (por projeto e
// Backoffice) — o chamador decide se identifica o ticket por categoria_id ou categoria_codigo.
function CartaoTicketViagem({ C, ticket, isAdmin, salvando, onSalvar }) {
  const [valor, setValor] = useState(ticket.ticket_medio);
  useEffect(() => { setValor(ticket.ticket_medio); }, [ticket.ticket_medio]);
  const nomeCurto = ticket.categoria_nome.replace(/^Viagens - /, "");
  const alterado = Number(valor) !== Number(ticket.ticket_medio);

  return (
    <div style={{ background: C.subcategoriaBg, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: C.muted }}>{nomeCurto}</label>
      <div style={{ display: "flex", gap: 4 }}>
        <input
          type="number" value={valor} disabled={!isAdmin || salvando}
          onChange={(e) => setValor(e.target.value)}
          style={{ ...inputStyle(C), padding: "6px 8px", opacity: isAdmin ? 1 : 0.6 }}
        />
        {isAdmin && alterado && (
          <button onClick={() => onSalvar(valor)} disabled={salvando} style={{ ...btnStyle(C.brand), padding: "6px 10px", fontSize: 11, whiteSpace: "nowrap" }}>
            {salvando ? "..." : "Salvar"}
          </button>
        )}
      </div>
    </div>
  );
}
